/**
 * @cfleet/api-client - Auth Hooks
 *
 * React Query hooks for authentication operations including
 * signup, email verification, and invitation acceptance.
 *
 * These hooks are for public endpoints (no auth required).
 *
 * @example
 * ```tsx
 * import { useSignup, useVerifyEmail, useInvitationByToken, useAcceptInvitation } from '@cfleet/api-client';
 *
 * // Signup flow
 * const { mutate: signup, isPending } = useSignup();
 * signup({ company: 'Acme', name: 'John', email: 'john@acme.com', password: 'secure123' });
 *
 * // Accept invitation flow
 * const { data: invitation } = useInvitationByToken(token);
 * const { mutate: acceptInvite } = useAcceptInvitation();
 * acceptInvite({ token, password: 'newPassword123' });
 * ```
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import type { AuthComponents } from '../generated';
import { createCoreClient, type ClientConfig } from '../index';

// ─────────────────────────────────────────────────────────────────────────────
// Type Aliases
// ─────────────────────────────────────────────────────────────────────────────

type SignupRequest = AuthComponents['schemas']['SignupRequest'];
type SignupResponse = AuthComponents['schemas']['SignupResponse'];
type EmailVerificationRequest = AuthComponents['schemas']['EmailVerificationRequest'];
type MessageResponse = AuthComponents['schemas']['MessageResponse'];
type InvitationDetails = AuthComponents['schemas']['InvitationDetails'];
type AcceptInviteRequest = AuthComponents['schemas']['AcceptInviteRequest'];
type AcceptInviteResponse = AuthComponents['schemas']['AcceptInviteResponse'];

// ─────────────────────────────────────────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Configuration for auth hooks
 */
export interface UseAuthHooksConfig {
  /** Custom client configuration (optional) */
  clientConfig?: ClientConfig;
}

// ─────────────────────────────────────────────────────────────────────────────
// useSignup
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to register a new user and organization.
 *
 * This is a public endpoint (no auth required).
 * After successful signup, a verification email is sent to the user.
 *
 * @param config - Optional configuration including custom client config
 * @returns Mutation hook for signup
 *
 * @example
 * ```tsx
 * const { mutate: signup, isPending, error } = useSignup();
 *
 * const handleSubmit = (formData: SignupFormData) => {
 *   signup({
 *     company: formData.company,
 *     name: formData.name,
 *     email: formData.email,
 *     password: formData.password,
 *     phone: formData.phone,         // optional
 *     industry: formData.industry,   // optional
 *     fleet_size: formData.fleetSize, // optional
 *   }, {
 *     onSuccess: (response) => {
 *       // response.verification_required will be true
 *       navigate('/login', { state: { signupSuccess: true, email: formData.email } });
 *     },
 *     onError: (error) => {
 *       // Handle error (e.g., duplicate email)
 *       toast.error(error.message);
 *     },
 *   });
 * };
 * ```
 */
export function useSignup(config?: UseAuthHooksConfig) {
  const client = createCoreClient(config?.clientConfig);

  return useMutation({
    mutationFn: async (data: SignupRequest): Promise<SignupResponse> => {
      const { data: response, error } = await client.POST('/auth/signup', {
        body: data,
      });

      if (error) {
        throw new Error(error.detail || 'Signup failed');
      }

      return response!;
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useVerifyEmail
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to verify email address after signup.
 *
 * This is a public endpoint (no auth required).
 * The token is obtained from the verification email link.
 *
 * @param config - Optional configuration including custom client config
 * @returns Mutation hook for email verification
 *
 * @example
 * ```tsx
 * const { mutate: verifyEmail, isPending, isSuccess } = useVerifyEmail();
 *
 * useEffect(() => {
 *   const token = searchParams.get('token');
 *   if (token) {
 *     verifyEmail({ token }, {
 *       onSuccess: () => {
 *         toast.success('Email verified!');
 *         navigate('/login');
 *       },
 *       onError: (error) => {
 *         toast.error(error.message);
 *       },
 *     });
 *   }
 * }, [searchParams]);
 * ```
 */
export function useVerifyEmail(config?: UseAuthHooksConfig) {
  const client = createCoreClient(config?.clientConfig);

  return useMutation({
    mutationFn: async (data: EmailVerificationRequest): Promise<MessageResponse> => {
      const { data: response, error } = await client.POST('/auth/verify-email', {
        body: data,
      });

      if (error) {
        throw new Error(error.detail || 'Email verification failed');
      }

      return response!;
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useInvitationByToken
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to get invitation details by token (public endpoint).
 *
 * Used on the accept-invite page to display invitation information
 * before the user sets their password. The invitation is scoped to
 * the tenant/organization that sent it.
 *
 * @param token - Invitation token from email link (null/empty disables query)
 * @param config - Optional configuration including custom client config
 * @returns Query hook with invitation details
 *
 * @example
 * ```tsx
 * const token = searchParams.get('token');
 * const { data: invitation, isLoading, error } = useInvitationByToken(token);
 *
 * if (isLoading) return <LoadingSpinner />;
 * if (error) return <ExpiredInvitationPage error={error.message} />;
 *
 * return (
 *   <AcceptInviteForm
 *     email={invitation.email}
 *     name={invitation.name}
 *     companyName={invitation.company_name}
 *     invitedBy={invitation.invited_by_name}
 *     role={invitation.role}
 *   />
 * );
 * ```
 */
export function useInvitationByToken(
  token: string | null | undefined,
  config?: UseAuthHooksConfig
) {
  const client = createCoreClient(config?.clientConfig);

  return useQuery({
    queryKey: ['invitation', 'token', token],
    queryFn: async (): Promise<InvitationDetails> => {
      if (!token) {
        throw new Error('No token provided');
      }

      const { data, error } = await client.GET('/auth/invitation/{token}', {
        params: { path: { token } },
      });

      if (error) {
        throw new Error(error.detail || 'Invitation not found');
      }

      return data!;
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes - invitation details don't change often
    retry: false, // Don't retry on 404/410 errors
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useAcceptInvitation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to accept an invitation and create an account.
 *
 * This creates the user account within the inviting tenant/organization
 * and returns login credentials. The user is automatically logged in
 * after acceptance.
 *
 * Note: The invitation token is scoped to a specific tenant. The user
 * will be added to that tenant with the role and permissions specified
 * in the invitation.
 *
 * @param config - Optional configuration including custom client config
 * @returns Mutation hook for accepting invitation
 *
 * @example
 * ```tsx
 * const { mutate: acceptInvite, isPending, error } = useAcceptInvitation();
 *
 * const handleAccept = (password: string) => {
 *   acceptInvite({ token, password }, {
 *     onSuccess: (response) => {
 *       // Auto-login: store tokens and navigate to dashboard
 *       setTokens(response.token, response.refresh_token, response.expires_in);
 *       navigate('/dashboard');
 *     },
 *     onError: (error) => {
 *       toast.error(error.message);
 *     },
 *   });
 * };
 * ```
 */
export function useAcceptInvitation(config?: UseAuthHooksConfig) {
  const client = createCoreClient(config?.clientConfig);

  return useMutation({
    mutationFn: async (data: AcceptInviteRequest): Promise<AcceptInviteResponse> => {
      const { data: response, error } = await client.POST('/auth/accept-invite', {
        body: data,
      });

      if (error) {
        throw new Error(error.detail || 'Failed to accept invitation');
      }

      return response!;
    },
  });
}
